import { Injectable } from '@angular/core';

/**
 * Zapis rysunku SVG (drzewo, wachlarz) do pliku PDF.
 *
 * Rysunek trafia na jedną stronę w postaci wektorowej, więc w czytniku można
 * go dowolnie powiększać. Eksport obejmuje cały bieżący układ - powiększenie
 * i przesunięcie widoku nie mają na niego wpływu.
 *
 * Trzy rzeczy trzeba przygotować przed konwersją:
 *  - style: svg2pdf czyta wyłącznie atrybuty i style inline, nie zna reguł
 *    z arkuszy komponentów, więc wygląd trzeba przepisać na klon,
 *  - obrazki: zdjęcia i herby wskazują na adresy backendu, których konwerter
 *    sam nie pobierze - zamieniamy je na dane osadzone,
 *  - czcionka: wbudowane czcionki PDF nie mają polskich znaków, więc osadzamy
 *    DejaVu Sans doczytywaną z assets. Ta akurat rodzina ma komplet znaków
 *    genealogicznych - symbol małżeństwa, gwiazdkę urodzenia i krzyż zgonu -
 *    których brakuje w popularniejszych krojach.
 */

/** Własności przenoszone z arkuszy na atrybuty klonu. */
const STYLE_PROPS = [
    'fill', 'fill-opacity', 'fill-rule',
    'stroke', 'stroke-width', 'stroke-opacity', 'stroke-dasharray', 'stroke-linecap', 'stroke-linejoin',
    'font-family', 'font-size', 'font-weight', 'font-style',
    'text-anchor', 'dominant-baseline', 'opacity', 'display', 'visibility'
];

/** Nazwa czcionki rejestrowanej w dokumencie PDF. */
const FONT_NAME = 'DejaVuSans';

/** Tymczasowy atrybut z szerokoscia napisu zmierzona na ekranie. */
const SCREEN_WIDTH_ATTR = 'data-szerokosc-ekranu';

/** Podpis programu w stopce wydruku. */
const PROGRAM_FOOTER = 'Wygenerowano w programie Genealogy Cards (https://genealogy.cards)';

const FONT_FILES: { plik: string; styl: 'normal' | 'bold' }[] = [
    { plik: 'assets/fonts/DejaVuSans.ttf', styl: 'normal' },
    { plik: 'assets/fonts/DejaVuSans-Bold.ttf', styl: 'bold' }
];

export interface PdfExportOptions {
    /** Nazwa pliku bez rozszerzenia. */
    fileName: string;
    /** Nagłówek wpisywany w metadane dokumentu. */
    title?: string;
    /** Podpis drukowany pod rysunkiem (np. nazwa drzewa i data). */
    footer?: string;
}

@Injectable()
export class PdfExportService {

    /** Czcionki czytane są raz na sesję - to ponad 800 kB. */
    private fontCache?: Promise<{ styl: string; base64: string; plik: string }[]>;

    /** Rejestracja czcionki w przeglądarce, też jednorazowa. */
    private fontFaceReady?: Promise<void>;

    /**
     * Nazwa pliku z rodzaju rysunku i opisu osoby: bez znaków, których nie
     * lubią systemy plików, bez podwójnych i końcowych myślników.
     */
    fileName(rodzaj: string, opis: string): string {
        const czysty = (opis || '')
            .replace(/\(.*?\)/g, '')            // lata w nawiasach tylko zaśmiecają nazwę
            .replace(/[^\p{L}\p{N}]+/gu, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase();
        return czysty ? `${rodzaj}-${czysty}` : rodzaj;
    }

    /**
     * Zapisuje wskazany rysunek SVG do pliku PDF. Zwraca false, gdy rysunek
     * jest pusty (nie ma czego eksportować).
     */
    async exportSvg(source: SVGSVGElement, options: PdfExportOptions): Promise<boolean> {
        const content = source.querySelector('g');
        if (!content) {
            return false;
        }
        // getBBox zwraca wymiary treści w jej własnym układzie, czyli bez
        // wpływu powiększenia i przesunięcia nałożonych na tę grupę
        const box = (content as SVGGraphicsElement).getBBox();
        if (!box.width || !box.height) {
            return false;
        }

        const [{ jsPDF }, { svg2pdf }, fonts] = await Promise.all([
            import('jspdf'),
            import('svg2pdf.js'),
            this.loadFonts()
        ]);

        // Przeglądarka musi znać tę samą czcionkę co dokument PDF: pozycje
        // znaków na łukach liczy ona, a rysuje je jsPDF. Bez tego litery
        // rozmieszczone według czcionki ekranowej zachodziły na siebie,
        // bo DejaVu jest wyraźnie szersza.
        await this.registerFontFace();

        const margin = 12;
        const clone = this.prepareClone(source, content, box, margin);
        document.body.appendChild(clone.holder);

        try {
            // zdjęcia i herby muszą być osadzone, zanim konwerter zacznie czytać klon
            await this.inlineImages(clone.svg);
            // klon wisi już w dokumencie, więc można mierzyć tekst i łuki
            this.matchTextWidths(clone.svg);
            this.flattenTextPaths(clone.svg);

            const width = box.width + 2 * margin;
            const height = box.height + 2 * margin;
            const pdf = new jsPDF({
                orientation: width >= height ? 'landscape' : 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            fonts.forEach((font) => {
                pdf.addFileToVFS(font.plik, font.base64);
                pdf.addFont(font.plik, FONT_NAME, font.styl);
            });
            pdf.setFont(FONT_NAME, 'normal');

            const page = { w: pdf.internal.pageSize.getWidth(), h: pdf.internal.pageSize.getHeight() };
            const pad = 10;
            // dwa wiersze stopki: opis drzewa z datą i podpis programu
            const stopka = 14;
            const skala = Math.min((page.w - 2 * pad) / width, (page.h - 2 * pad - stopka) / height);
            const rysunek = { w: width * skala, h: height * skala };

            await svg2pdf(clone.svg, pdf, {
                x: (page.w - rysunek.w) / 2,
                y: (page.h - stopka - rysunek.h) / 2,
                width: rysunek.w,
                height: rysunek.h
            });

            pdf.setFont(FONT_NAME, 'normal');
            pdf.setTextColor(120);
            if (options.footer) {
                pdf.setFontSize(8);
                pdf.text(options.footer, page.w / 2, page.h - 11, { align: 'center' });
            }
            pdf.setFontSize(7);
            pdf.text(PROGRAM_FOOTER, page.w / 2, page.h - 6, { align: 'center' });

            pdf.setProperties({ title: options.title ?? options.fileName });
            pdf.save(options.fileName + '.pdf');
            return true;
        } finally {
            clone.holder.remove();
        }
    }

    /**
     * Klon rysunku bez powiększenia widoku, z wklejonymi stylami i obrazkami
     * osadzonymi w dokumencie. Klon musi wisieć w drzewie strony, bo svg2pdf
     * odpytuje go o wymiary - trzyma go więc kontener poza ekranem.
     */
    private prepareClone(source: SVGSVGElement, content: Element, box: DOMRect, margin: number) {
        const svg = source.cloneNode(true) as SVGSVGElement;

        // pozycje elementów liczone są od nowa, więc pan i zoom muszą zniknąć
        const contentClone = svg.querySelector('g');
        contentClone?.removeAttribute('transform');

        svg.setAttribute('viewBox',
            `${box.x - margin} ${box.y - margin} ${box.width + 2 * margin} ${box.height + 2 * margin}`);
        svg.setAttribute('width', String(box.width + 2 * margin));
        svg.setAttribute('height', String(box.height + 2 * margin));
        svg.removeAttribute('style');
        svg.removeAttribute('class');

        this.copyStyles(source, svg);

        // Na wydruku nie zostaje nic, w co dało się kliknąć: przycisk menu
        // karty, puste miejsca '+' do dopinania krewnych i pigułki '+N'
        // doładowania ukrytych krewnych. Na papierze wszystkie trzy wyglądają
        // jak przyciski, a żadnego nie da się nacisnąć.
        //
        // Usuwamy je PO copyStyles, bo tamto paruje źródło z klonem po
        // kolejności - skrócenie listy wcześniej rozjechałoby rozmiary napisów
        // w całym drzewie.
        svg.querySelectorAll('.card-menu-btn, .add-slot, .expander, title')
            .forEach((el) => el.remove());

        const holder = document.createElement('div');
        holder.style.cssText = 'position:fixed;left:-10000px;top:0;opacity:0;pointer-events:none;';
        holder.appendChild(svg);
        return { svg, holder };
    }

    /** Przepisanie wyliczonych stylów na atrybuty - klon jest poza arkuszami. */
    private copyStyles(source: Element, target: Element) {
        const zrodla = [source, ...Array.from(source.querySelectorAll('*'))];
        const cele = [target, ...Array.from(target.querySelectorAll('*'))];

        zrodla.forEach((el, index) => {
            const cel = cele[index];
            if (!cel) {
                return;
            }
            const computed = window.getComputedStyle(el);
            STYLE_PROPS.forEach((prop) => {
                const wartosc = computed.getPropertyValue(prop);
                if (wartosc && wartosc !== 'auto' && wartosc !== 'normal') {
                    cel.setAttribute(prop, wartosc);
                }
            });
            // svg2pdf dobiera czcionkę po nazwie rodziny - podstawiamy tę osadzoną
            if (cel.tagName === 'text' || cel.tagName === 'tspan' || cel.tagName === 'textPath') {
                // Szerokość z ekranu zapisujemy na elemencie, dopóki pary
                // źródło-klon są jeszcze zgodne. Późniejsze pasowanie liczy
                // tylko na tym atrybucie, więc usuwanie czegokolwiek z klonu
                // nie może go już rozstroić.
                try {
                    const szerokosc = (el as unknown as SVGTextContentElement).getComputedTextLength();
                    if (szerokosc > 0) {
                        cel.setAttribute(SCREEN_WIDTH_ATTR, szerokosc.toFixed(2));
                    }
                } catch {
                    // element bez tekstu - nie ma czego mierzyć
                }
                cel.setAttribute('font-family', FONT_NAME);
                // Osadzone są tylko dwie odmiany. Pośrednie grubości (np. 600
                // w wachlarzu) nie pasowały do żadnej z nich i tekst cichaczem
                // wracał do czcionki wbudowanej, gubiąc polskie znaki.
                const grubosc = parseInt(computed.getPropertyValue('font-weight'), 10);
                cel.setAttribute('font-weight', grubosc >= 550 ? 'bold' : 'normal');
            }
        });
    }

    /**
     * Dopasowanie szerokości napisów do wersji ekranowej.
     *
     * Rozmiary czcionek w widokach dobierane są tak, by napis zmieścił się
     * w karcie albo w sektorze - liczone czcionką ekranową. DejaVu, którą
     * rysuje PDF, jest od niej szersza, więc te same napisy wychodziły poza
     * swoje pola (najbardziej rzucały się w oczy nazwiska wystające
     * z pierścieni wachlarza). Każdy napis dostaje więc rozmiar zmniejszony
     * w proporcji, w jakiej urósł - dzięki temu zajmuje dokładnie tyle miejsca
     * co na ekranie.
     */
    private matchTextWidths(target: Element) {
        Array.from(target.querySelectorAll(`[${SCREEN_WIDTH_ATTR}]`)).forEach((el) => {
            const ekran = parseFloat(el.getAttribute(SCREEN_WIDTH_ATTR) || '');
            el.removeAttribute(SCREEN_WIDTH_ATTR);
            const mierzalny = el as unknown as SVGTextContentElement;
            if (!ekran || typeof mierzalny.getComputedTextLength !== 'function') {
                return;
            }
            let pdf = 0;
            try {
                pdf = mierzalny.getComputedTextLength();
            } catch {
                return;
            }
            if (!pdf || pdf <= ekran) {
                return;
            }
            const rozmiar = parseFloat(window.getComputedStyle(el).fontSize);
            if (rozmiar > 0) {
                el.setAttribute('font-size', (rozmiar * ekran / pdf).toFixed(2));
            }
        });
    }

    /**
     * Zamiana tekstu biegnącego po łuku na pojedyncze znaki - svg2pdf nie zna
     * textPath i po prostu go gubi (w wachlarzu znikały wszystkie podpisy
     * pierścieni).
     *
     * Każdy znak dostaje pozycję i obrót policzone przez samą przeglądarkę
     * (getStartPositionOfChar / getRotationOfChar), więc napis wygina się
     * dokładnie tak jak na ekranie. Wcześniejsza wersja stawiała cały napis
     * prosto, stycznie do środka łuku - czytelnie, ale wyraźnie inaczej niż
     * w widoku, bo litery nie podążały za krzywizną pierścienia.
     */
    private flattenTextPaths(svg: SVGSVGElement) {
        const NS = 'http://www.w3.org/2000/svg';

        Array.from(svg.querySelectorAll('textPath')).forEach((textPath) => {
            const rodzic = textPath.parentElement as unknown as SVGTextElement | null;
            const znaki = textPath as unknown as SVGTextContentElement;
            if (!rodzic || typeof znaki.getNumberOfChars !== 'function') {
                return;
            }
            const tresc = textPath.textContent || '';
            const ile = znaki.getNumberOfChars();
            if (!ile) {
                return;
            }

            const grupa = document.createElementNS(NS, 'g');
            for (let i = 0; i < ile; i++) {
                const znak = tresc[i];
                if (!znak || znak === ' ') {
                    continue;
                }
                let punkt: DOMPoint;
                let obrot: number;
                try {
                    punkt = znaki.getStartPositionOfChar(i);
                    obrot = znaki.getRotationOfChar(i);
                } catch {
                    continue;   // znak poza długością łuku
                }
                const litera = document.createElementNS(NS, 'text');
                litera.textContent = znak;
                litera.setAttribute('x', String(punkt.x));
                litera.setAttribute('y', String(punkt.y));
                litera.setAttribute('transform', `rotate(${obrot.toFixed(2)} ${punkt.x} ${punkt.y})`);
                litera.setAttribute('text-anchor', 'start');
                ['font-family', 'font-size', 'font-weight', 'fill'].forEach((prop) => {
                    const wartosc = textPath.getAttribute(prop) || rodzic.getAttribute(prop);
                    if (wartosc) {
                        litera.setAttribute(prop, wartosc);
                    }
                });
                grupa.appendChild(litera);
            }
            rodzic.parentNode?.insertBefore(grupa, rodzic);
        });

        // pierwotne napisy po łukach nic już nie wnoszą
        Array.from(svg.querySelectorAll('textPath')).forEach((tp) => tp.parentElement?.remove());
    }

    /** Zdjęcia i herby jako dane osadzone - konwerter nie pobiera ich sam. */
    private async inlineImages(svg: SVGSVGElement): Promise<void> {
        const obrazki = Array.from(svg.querySelectorAll('image'));
        await Promise.all(obrazki.map(async (img) => {
            const href = img.getAttribute('href') || img.getAttribute('xlink:href');
            if (!href || href.startsWith('data:')) {
                return;
            }
            try {
                const odpowiedz = await fetch(href, { credentials: 'include' });
                const blob = await odpowiedz.blob();
                const dane = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result));
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
                img.setAttribute('href', dane);
                img.removeAttribute('xlink:href');
            } catch {
                // niedostępny obrazek nie może zablokować całego eksportu
                img.remove();
            }
        }));
    }

    /** Ta sama czcionka co w PDF, udostępniona przeglądarce do pomiarów. */
    private async registerFontFace(): Promise<void> {
        if (this.fontFaceReady || typeof FontFace === 'undefined' || !document.fonts) {
            return this.fontFaceReady;
        }
        this.fontFaceReady = Promise.all(FONT_FILES.map(async (font) => {
            const face = new FontFace(FONT_NAME, `url(${font.plik})`, { weight: font.styl });
            await face.load();
            document.fonts.add(face);
        })).then(() => undefined);
        return this.fontFaceReady;
    }

    private loadFonts() {
        if (!this.fontCache) {
            this.fontCache = Promise.all(FONT_FILES.map(async (font) => ({
                styl: font.styl,
                plik: font.plik.split('/').pop() as string,
                base64: await this.fetchAsBase64(font.plik)
            })));
        }
        return this.fontCache;
    }

    private async fetchAsBase64(url: string): Promise<string> {
        const odpowiedz = await fetch(url);
        const bufor = await odpowiedz.arrayBuffer();
        let binarne = '';
        const bajty = new Uint8Array(bufor);
        const krok = 0x8000;
        for (let i = 0; i < bajty.length; i += krok) {
            binarne += String.fromCharCode(...bajty.subarray(i, i + krok));
        }
        return btoa(binarne);
    }
}
